import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { fetchProfile, updateProfile } from '../store/features/profileSlice';

const UserProfile = () => {
  const dispatch = useDispatch();
  const { profileData, loading, error } = useSelector((state) => state.profile); // Use profile slice state
  const [profileImage, setProfileImage] = useState(null); // State for profile image
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    profile_image: "",
    full_name: "",
    email: "",
    job_role: "",
    department: "",
    location: "",
  });

  // Fetch profile data on component mount
  useEffect(() => {
    dispatch(fetchProfile());
  }, [dispatch]);

  // Update form data when profileData changes
  useEffect(() => {
    if (profileData) {
      setFormData({
        profile_image: profileImage || "",
        full_name: profileData.full_name || "",
        email: profileData.email || "",
        job_role: profileData.job_role || "",
        department: profileData.department || "",
        location: profileData.location || "",
      });
    }
  }, [profileData]);

  // Handle file input change for profile image
  const handleFileChange = (e) => {
    setProfileImage(e.target.files[0]);
  };


  // Handle input change for profile fields
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Handle save profile
  const handleSaveProfile = () => {
    dispatch(updateProfile(formData)).then(() => {
      setIsEditing(false); // Exit edit mode after saving
    });
  };

  // Handle edit toggle
  const handleEditToggle = () => {
    setIsEditing(!isEditing);
  };

  if (loading) return <div>Loading user profile...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div className="w-full max-w-6xl bg-bg-dashboard rounded-lg flex gap-8 p-8">
      {/* Left Side */}
      <div className="w-1/3 flex flex-col items-center bg-white rounded-md shadow-md justify-center border border-gray-200">
        <div className="relative w-40 h-40 border-2 border-gray-300 rounded-full flex items-center justify-center">
          <img
            src={profileImage||"./userplaceholder.jpg"}
            alt="Profile"
            className="rounded-full w-full h-full object-cover"
          />
          {isEditing && (
            <label
              className="absolute bottom-0 right-0 w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center border border-gray-300 hover:bg-gray-200 cursor-pointer"
              title="Upload profile picture"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                className="w-5 h-5 text-gray-600"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M15.232 5.232l3.536 3.536m-2.036-4.536A2.5 2.5 0 1120.5 8.964L9 20.464l-4 1 1-4L15.232 5.232z"
                />
              </svg>
              <input
                type="file"
                name="profile_image"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
            </label>
          )}
        </div>
        <h3 className="text-xl font-medium mt-6 text-center">{formData.full_name}</h3>
        <p className="text-gray-600 text-center mt-2">Job Role: {formData.job_role}</p>
      </div>

      {/* Right Side */}
      <div className="w-2/3 border rounded-md shadow-md bg-white border-gray-200 p-8">
        <div className="space-y-6">
          {/* Profile Details */}
          {["full_name", "email", "job_role", "department", "location"].map((field) => (
            <div key={field} className="grid grid-cols-3 items-center border-b border-gray-200 pb-4">
              <span className="font-medium text-gray-700">
                {field.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())}
              </span>
              <span className="col-span-2">
                {isEditing ? (
                  <input
                    type="text"
                    name={field}
                    value={formData[field]}
                    onChange={handleInputChange}
                    className="w-full border rounded-md px-2 py-1 text-sm"
                  />
                ) : (
                  <span>{formData[field]}</span>
                )}
              </span>
            </div>
          ))}
        </div>

        {/* Edit Button */}
        <div className="mt-6 flex justify-end">
          <button
            onClick={isEditing ? handleSaveProfile : handleEditToggle}
            className={`px-6 py-2 rounded-lg text-white ${
              isEditing
                ? "bg-logo hover:bg-logo-darker"
                : "bg-logo hover:bg-logo-darker"
            }`}
          >
            {isEditing ? "Save" : "Edit Profile"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default UserProfile;
